import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Palette, Printer, Boxes, Truck, ShieldCheck, Zap, Users } from "lucide-react";

/**
 * Partner hub page — four typed intake funnels.
 *
 * /partner           → this hub
 * /partner/studio    → Design Studio form (existing flow, unchanged)
 * /partner/printer   → Printer / Converter form (new)
 * /partner/material  → Material Supplier form (new) — opens the Phase 2 role
 * /partner/logistics → Logistics / Finishing form (new)
 */
export default function Partner() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tiles = [
    {
      slug: "studio",
      icon: <Palette size={22} strokeWidth={1.8}/>,
      kicker: "DESIGN STUDIOS & AGENCIES",
      title: "Join as a Design Agency",
      body: "You design packaging, labels, logos or marketing collateral. We route escrow-funded briefs that match your specialties and tier.",
      ctaText: "Apply as a studio",
      hue: "#A855F7", bg: "#F5F3FF",
    },
    {
      slug: "printer",
      icon: <Printer size={22} strokeWidth={1.8}/>,
      kicker: "PRINTERS & CONVERTERS",
      title: "Join as a Printer / Converter",
      body: "Sheet-fed offset, digital, flexo, gravure, screen, flexible packaging, labels — if you print it, we route sealed-bid auctions straight to you with escrow protection and blind-shipped dispatch.",
      ctaText: "Apply as a printer",
      hue: "#2563EB", bg: "#EFF6FF",
      highlighted: true,
    },
    {
      slug: "material",
      icon: <Boxes size={22} strokeWidth={1.8}/>,
      kicker: "RAW MATERIAL SUPPLIERS",
      title: "Join as a Material Supplier",
      body: "You supply paper, boards, flex-film, inks, plates or adhesives. Buyers on our platform post sealed RFQs — you quote, the cheapest with the right SLA wins.",
      ctaText: "Apply as a supplier",
      hue: "#10B981", bg: "#ECFDF5",
      badge: "NEW",
    },
    {
      slug: "logistics",
      icon: <Truck size={22} strokeWidth={1.8}/>,
      kicker: "LOGISTICS & FINISHING",
      title: "Join as a Finishing Partner",
      body: "Die-cutting, lamination, pouching, binding, last-mile — slot in on specific orders where a buyer needs your service to complete the job.",
      ctaText: "Apply as a finisher",
      hue: "#F59E0B", bg: "#FFFBEB",
    },
  ];

  return (
    <div className="m-shell" data-testid="partner-hub-page">
      <header className="m-nav" data-testid="partner-navbar">
        <Link to="/" className="m-brand" data-testid="partner-brand-home">
          <img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/>
        </Link>
        <nav className="m-nav-links">
          <Link to="/" data-testid="partner-nav-home">Home</Link>
          <Link to="/contact" data-testid="partner-nav-contact">Contact</Link>
          <Link to="/partner" data-testid="partner-nav-partner">Partner with us</Link>
          <Link to="/careers" data-testid="partner-nav-careers">Careers</Link>
        </nav>
        <div className="m-nav-actions">
          <Link to="/login" className="m-ghost" data-testid="partner-nav-signin">Sign in</Link>
          <Link to="/register" className="m-cta" data-testid="partner-nav-launch">Get started <ArrowRight size={14}/></Link>
        </div>
      </header>

      <section className="pt-hero" data-testid="partner-hub-hero">
        <p className="eyebrow">PARTNER WITH GOPRINTPACK™</p>
        <h1 className="pt-h1">
          Four doors in. <em>One</em> escrow-protected, GST-compliant platform.
        </h1>
        <p className="pt-sub">
          340+ brands run their printing and packaging through us. Pick the lane that matches your business — we onboard you in 48 hours, no sign-up fees, no exclusivity.
        </p>
        <div className="pt-strip">
          <Chip icon={<ShieldCheck size={14}/>} text="Escrow before every payout"/>
          <Chip icon={<Zap size={14}/>} text="48-hour onboarding"/>
          <Chip icon={<Users size={14}/>} text="340+ active brand buyers"/>
        </div>
      </section>

      <section className="pt-tiles" data-testid="partner-tiles">
        {tiles.map(t => (
          <Link
            key={t.slug}
            to={`/partner/${t.slug}`}
            className={`pt-tile ${t.highlighted ? "pt-tile-hi" : ""}`}
            data-testid={`partner-tile-${t.slug}`}
          >
            <div className="pt-tile-icon" style={{background:t.bg,color:t.hue}}>{t.icon}</div>
            {t.badge && <span className="pt-tile-badge" style={{background:t.hue}}>{t.badge}</span>}
            <p className="pt-tile-kicker" style={{color:t.hue}}>{t.kicker}</p>
            <h3 className="pt-tile-title">{t.title}</h3>
            <p className="pt-tile-body">{t.body}</p>
            <span className="pt-tile-cta">
              {t.ctaText} <ArrowRight size={14}/>
            </span>
          </Link>
        ))}
      </section>

      <section className="pt-faq" data-testid="partner-faq">
        <p className="eyebrow">QUICK ANSWERS</p>
        <h2 className="pt-h2">What to expect once you apply.</h2>
        <div className="pt-faq-grid">
          {FAQS.map((f, i) => (
            <div className="pt-faq-item" key={i} data-testid={`partner-faq-${i}`}>
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="pt-foot">
        <p>Questions? Write to <a href="mailto:info@goprintpack.com">info@goprintpack.com</a>.</p>
        <p className="pt-foot-small">© Tejas Industries. GoPrintPack™ is a trademark of Tejas Industries.</p>
      </footer>
    </div>
  );
}

function Chip({ icon, text }) {
  return (
    <span className="pt-chip">
      <span className="pt-chip-icon">{icon}</span>
      {text}
    </span>
  );
}

const FAQS = [
  { q: "Does it cost anything to apply?",
    a: "No. Applying is free. We charge a per-order platform commission only on jobs you actually win — no listing fees, no monthly minimums." },
  { q: "How long does onboarding take?",
    a: "Most verified partners are live within 48 hours of applying. We'll call you to walk through KYC, bank onboarding (Razorpay Route for payouts) and capability tagging." },
  { q: "Can I partner in multiple roles?",
    a: "Yes — e.g. a printer who also carries board stock. Submit a separate application per role so we can verify each capability independently." },
  { q: "Is identity exposure a risk?",
    a: "No. Printers stay anonymised to buyers, and all B2B shipments are blind-shipped via Shiprocket under the GoPrintPack logistics identity." },
];
